import React, { useEffect, useState } from 'react';

import StepShell from './StepShell';

import FormInput from 'CommonComponents/FormInput';
import IconButton from 'CommonComponents/IconButton';
import TextButton from 'CommonComponents/TextButton';
import ToggleInput from 'CommonComponents/ToggleInput';

import { useToast } from 'Hooks/useToast';

import TrashIcon from 'Assets/icons/trash.svg';

const MARK_UP = 'mark_up_enabled';
const CATEGORIES = 'mark_up_categories';

/**
 * Functionality. Every toggle arrives from the step's data endpoint carrying
 * its own copy, value and lock state, already localised — the page renders
 * what it is given and holds no copy of its own.
 *
 * Only the toggles this organisation is entitled to are sent, so a toggle
 * absent from the list is not rendered.
 *
 * Mark-up also carries the current budget's categories, listed read-only, and
 * new ones added here are created by the same save that switches it on. They
 * are submitted only while mark-up is on — the server refuses them otherwise.
 *
 * A locked toggle is shown disabled with its reason. That is an affordance on
 * top of the server's refusal, never a replacement for it — the update
 * endpoint rejects the same change with the same sentence.
 */
export default function FuncStep({ step, urls, deferring, onDefer, onSaved }) {
    const [toggles, setToggles] = useState(null);
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [existingCategories, setExistingCategories] = useState([]);
    const [newCategories, setNewCategories] = useState([]);
    const toast = useToast();

    useEffect(() => {
        axios
            .get(urls.data)
            .then(({ data }) => {
                const list = data.toggles ?? [];
                setToggles(list);
                setValues(list.reduce((carry, item) => ({ ...carry, [item.key]: !!item.enabled }), {}));
                setExistingCategories(data.mark_up_categories ?? []);
            })
            .catch(() => toast.error($t('Something went wrong. Please try again.')));
    }, [urls.data]);

    const toggle = (key) => {
        setErrors((current) => {
            const next = { ...current };
            delete next[key];

            return next;
        });
        setValues((current) => ({ ...current, [key]: !current[key] }));
    };

    // Row errors are keyed by index ('mark_up_categories.1.name'), so any change
    // to the rows drops them all rather than leave one against the wrong row.
    const clearCategoryErrors = () =>
        setErrors((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== CATEGORIES && !key.startsWith(`${CATEGORIES}.`))));

    const addCategory = () => {
        clearCategoryErrors();
        setNewCategories((current) => [...current, { name: '', info: '' }]);
    };

    const updateCategory = (index, field, value) => {
        clearCategoryErrors();
        setNewCategories((current) => current.map((category, i) => (i === index ? { ...category, [field]: value } : category)));
    };

    const removeCategory = (index) => {
        clearCategoryErrors();
        setNewCategories((current) => current.filter((_, i) => i !== index));
    };

    const handleNext = () => {
        setSaving(true);
        setErrors({});

        const payload = values[MARK_UP] && newCategories.length > 0 ? { ...values, [CATEGORIES]: newCategories } : values;

        axios
            .put(urls.update, payload)
            .then(({ data }) => onSaved(data))
            .catch((e) => {
                // No Formik form here to hand the errors to, so a validation
                // failure is surfaced as its own message and against the toggle
                // that was refused.
                const responseErrors = e.response?.data?.errors;
                setErrors(responseErrors ?? {});
                toast.error(responseErrors ? Object.values(responseErrors).flat().join(' ') : $t('Something went wrong. Please try again.'));
            })
            .finally(() => setSaving(false));
    };

    if (!toggles) {
        return (
            <StepShell step={step} deferring={deferring} saving={true} onDefer={onDefer} onNext={() => {}} nextLabel={$t('Save and continue')}>
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            </StepShell>
        );
    }

    const categoryError = (index, field) => (errors[`${CATEGORIES}.${index}.${field}`] ?? []).join(' ');

    const renderMarkUpCategories = () => (
        <div className="tw-flex tw-flex-col tw-gap-y-3 tw-mt-2">
            {existingCategories.length > 0 && (
                <ul className="tw-flex tw-flex-col tw-gap-y-1 tw-mb-0 tw-ps-0 tw-list-none">
                    {existingCategories.map((category) => (
                        <li key={category.id} className="tw-text-sm tw-text-charcoal">
                            {category.name}
                            {category.info && <span className="tw-ms-2 tw-text-2xs tw-text-charcoal-50">{category.info}</span>}
                        </li>
                    ))}
                </ul>
            )}

            {!step.read_only && (
                <>
                    {newCategories.map((category, index) => (
                        <div key={index} className="tw-flex tw-items-start tw-gap-x-2">
                            <div className="tw-flex tw-flex-col tw-gap-y-2 tw-flex-1">
                                <FormInput
                                    id={`mark_up_category_${index}_name`}
                                    name={`mark_up_category_${index}_name`}
                                    type="text"
                                    label={$t('Mark-up category name')}
                                    required={true}
                                    value={category.name}
                                    showError={!!categoryError(index, 'name')}
                                    errorText={categoryError(index, 'name')}
                                    onChange={(e) => updateCategory(index, 'name', e.target.value)}
                                />
                                <FormInput
                                    id={`mark_up_category_${index}_info`}
                                    name={`mark_up_category_${index}_info`}
                                    type="text"
                                    label={$t('Info')}
                                    value={category.info}
                                    showError={!!categoryError(index, 'info')}
                                    errorText={categoryError(index, 'info')}
                                    onChange={(e) => updateCategory(index, 'info', e.target.value)}
                                />
                            </div>
                            <IconButton
                                className="tw-mt-6"
                                variant="danger"
                                size="sm"
                                ghost
                                title={$t('Remove')}
                                aria-label={$t('Remove')}
                                onClick={() => removeCategory(index)}
                            >
                                <TrashIcon className="tw-w-3 tw-h-3" />
                            </IconButton>
                        </div>
                    ))}

                    <TextButton
                        variant="primary"
                        className="tw-w-full tw-rounded-lg tw-border tw-border-dashed tw-border-charcoal-25 hover:tw-border-blue tw-bg-transparent tw-px-4 tw-py-2"
                        onClick={addCategory}
                    >
                        {$t('+ Add a mark-up category')}
                    </TextButton>
                </>
            )}

            {errors[CATEGORIES] && <p className="tw-text-2xs tw-text-red tw-mb-0">{errors[CATEGORIES].join(' ')}</p>}
        </div>
    );

    // Grouped in the order the server sent them, so the enum decides both the
    // group headings and where each toggle sits under them.
    const groups = toggles.reduce((carry, item) => {
        const existing = carry.find((group) => group.key === item.group);
        if (existing) {
            existing.items.push(item);

            return carry;
        }

        return [...carry, { key: item.group, label: item.group_label, items: [item] }];
    }, []);

    return (
        <StepShell
            step={step}
            lede={$t(
                'Extra capabilities, grouped by theme, each with what it does and why you might use it. All off by default; each can be turned on later without redoing anything.',
            )}
            deferring={deferring}
            saving={saving}
            onDefer={onDefer}
            onNext={handleNext}
            nextDisabled={step.read_only}
            nextLabel={$t('Save and continue')}
        >
            <div className="tw-flex tw-flex-col tw-gap-y-6">
                {groups.map((group) => (
                    <div key={group.key}>
                        <h3 className="tw-text-sm tw-font-semibold tw-text-charcoal tw-mb-3">{group.label}</h3>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                            {group.items.map((item) => (
                                <div key={item.key} className="tw-p-4 tw-rounded-lg tw-border tw-border-charcoal-15 tw-flex tw-flex-col tw-gap-y-2">
                                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-x-4">
                                        <h4 className="tw-text-sm tw-font-semibold tw-text-charcoal tw-mb-0">{item.label}</h4>
                                        <ToggleInput
                                            noBottomSpacing={true}
                                            // ToggleInput renders no visible label here, so the
                                            // card title is the control's accessible name.
                                            aria-label={item.label}
                                            checked={!!values[item.key]}
                                            disabled={step.read_only || item.disabled}
                                            onChange={() => toggle(item.key)}
                                            tooltip={item.disabled_reason || undefined}
                                        />
                                    </div>
                                    <p className="tw-text-2xs tw-text-charcoal-50 tw-mb-0">{item.description}</p>
                                    {item.disabled && item.disabled_reason && (
                                        <p className="tw-text-2xs tw-font-medium tw-text-charcoal-75 tw-mb-0">{item.disabled_reason}</p>
                                    )}
                                    {errors[item.key] && <p className="tw-text-2xs tw-text-red tw-mb-0">{errors[item.key].join(' ')}</p>}
                                    {item.key === MARK_UP && !!values[MARK_UP] && renderMarkUpCategories()}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </StepShell>
    );
}
